import type { PlanPerson, ProjectEvent, ProjectPackage, ProjectTask } from "./project-plan";

// Reine Zeitrechnung für die Personfokus-Swimlane. Bewusst ohne React und ohne
// Formatierung: die Screens entscheiden, wie ein Balken aussieht, diese Datei
// entscheidet nur, wo er liegt. Dadurch lässt sich die Geometrie prüfen, ohne
// etwas zu rendern, und beide Aufrufer (Aufteilen-Vorschau und Timeline)
// rechnen garantiert dasselbe.

const DAY_MS = 24 * 60 * 60 * 1000;

export interface ScheduleRange {
  startMs: number;
  endMs: number;
}

export interface LaneBar {
  /** Stabile Id des Balkens: die der Aufgabe, beim Paketbalken die des Pakets. */
  taskId: string;
  /**
   * Ein Paket ist selbst ein Balken, nicht nur der Rahmen um seine Aufgaben.
   * Sonst ist ein Paket ohne Aufgaben auf der Zeitachse unsichtbar - und genau
   * die frisch angelegten sind das.
   */
  kind: "package" | "task";
  title: string;
  /** Nur beim Aufgabenbalken gesetzt; der Bearbeitungskasten hängt daran. */
  task: ProjectTask | null;
  /** Index des Abschnitts, aus dem der Balken stammt - trägt die Einfärbung. */
  packageIndex: number;
  packageId: string;
  leftPercent: number;
  widthPercent: number;
}

/**
 * Ein Paket und die Aufgaben daraus, die in DIESER Spur liegen. Die Gruppe ist
 * die Klammer, die die Zeichnung zeigt: Aufgaben liegen im Paket, also gehören
 * sie sichtbar zusammen.
 *
 * Eine Gruppe ohne eigene Paketzeile ist möglich und richtig - das Paket gehört
 * dann jemand anderem, nur eine seiner Aufgaben liegt hier.
 */
export interface LaneGroup {
  packageId: string;
  /** Der Paketbalken, wenn das Paket dieser Spur gehört. */
  packageBar: LaneBar | null;
  /**
   * Zeilen innerhalb der Gruppe. Zwei Aufgaben derselben Person, die sich
   * zeitlich überschneiden, dürfen nicht übereinander liegen - sonst verdeckt
   * die eine die andere und die Überschneidung, also genau die interessante
   * Information, wird unsichtbar. Sie bekommen deshalb je eine eigene Zeile.
   */
  rows: LaneBar[][];
}

export interface Lane {
  /** null = noch niemandem zugewiesen. */
  personId: string | null;
  name: string;
  /** Je Paket eine Gruppe, in der Reihenfolge der Pakete im Plan. */
  groups: LaneGroup[];
}

export function dayMs(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

/**
 * Gesamtspanne über Pakete und Termine; leer = null, dann gibt es nichts zu
 * zeichnen. Termine zählen mit, sonst läge eine Abgabefrist hinter dem letzten
 * Paket ausserhalb der Achse und wäre unsichtbar - ausgerechnet der Punkt, auf
 * den alles zuläuft.
 */
export function scheduleRange(
  packages: readonly ProjectPackage[],
  events: readonly ProjectEvent[] = [],
): ScheduleRange | null {
  const eventDays = events.map((e) => dayMs(e.date)).filter(Number.isFinite);
  const starts = [...packages.map((p) => dayMs(p.start)).filter(Number.isFinite), ...eventDays];
  const ends = [...packages.map((p) => dayMs(p.end)).filter(Number.isFinite), ...eventDays];
  if (!starts.length || !ends.length) return null;
  // Die Achse beginnt am MONTAG der Woche, in der das Erste liegt. Zwei Gründe:
  // links entsteht Puffer, statt dass der erste Balken an der Kante klebt, und
  // die Wochenmarken fallen mit dem Achsenanfang zusammen - sonst steht die
  // erste Kalenderwoche angeschnitten da und ihre Zahl hängt im Nichts.
  const startMs = isoWeekStart(Math.min(...starts));
  // Ein Paket, das am 1. endet, belegt den 1. noch ganz - deshalb schliesst die
  // Achse den letzten Tag mit ein, sonst faellt ein eintaegiges Paket auf 0 Breite.
  const endMs = Math.max(...ends) + DAY_MS;
  return { startMs, endMs: endMs > startMs ? endMs : startMs + DAY_MS };
}

/**
 * Streckt den Bereich auf mindestens `days` Tage, indem hinten angehängt wird.
 *
 * Der Zoom verlangt das: "Klein" heisst, viele Wochen nebeneinander zu sehen,
 * und ein Projekt von zwei Wochen hat diese Wochen nicht. Ohne Auffüllen zeigte
 * jede Stufe dieselben zwei Kalenderwochen, nur unterschiedlich breit - genau
 * der Eindruck, dass die Einstellung nichts tut.
 *
 * Angehängt wird hinten, nicht symmetrisch: ein Plan beginnt links mit dem, was
 * schon feststeht, und die freie Fläche rechts ist die Zukunft, in die er
 * wächst. Vorne Luft zu lassen schöbe den Anfang grundlos in die Mitte.
 */
export function extendRange(range: ScheduleRange, days: number): ScheduleRange {
  const wanted = range.startMs + Math.max(1, days) * DAY_MS;
  return wanted > range.endMs ? { startMs: range.startMs, endMs: wanted } : range;
}

/** Tage der Gesamtspanne - Grundlage für die Mindestbreite der Zeitachse. */
export function scheduleDays(range: ScheduleRange): number {
  return Math.max(1, Math.round((range.endMs - range.startMs) / DAY_MS));
}

export function percentFor(ms: number, range: ScheduleRange): number {
  const span = range.endMs - range.startMs;
  return ((ms - range.startMs) / span) * 100;
}

/**
 * Eine Spur je Person, in der Reihenfolge von `people`, plus eine Spur für
 * alles Unzugewiesene. `people` sind die am Projekt Beteiligten, nicht das
 * Adressbuch: eine Spur je Kontakt wären hundert leere Zeilen. Eine Spur ohne
 * Balken bleibt trotzdem stehen, solange die Person in der Liste steht - sie
 * sagt "diese Person hat gerade nichts", und das will man sehen.
 */
export function buildLanes(
  packages: readonly ProjectPackage[],
  people: readonly PlanPerson[],
  unassignedLabel: string,
  /**
   * Der Bereich, auf den die Balken gerechnet werden. Er MUSS derselbe sein, auf
   * dem die Achse steht: rechnete er ihn selbst aus, fehlten ihm die Termine und
   * die Zoomstufe, und ein Balken landete bei 100 Prozent einer Spanne, die die
   * Achse gar nicht zeigt - sichtbar als Balken, der neben seinem Datum liegt.
   */
  range: ScheduleRange | null,
): Lane[] {
  const collected = new Map<string | null, LaneBar[]>();
  for (const person of people) collected.set(person.id, []);
  collected.set(null, []);

  if (range) {
    const place = (
      id: string,
      kind: "package" | "task",
      title: string,
      task: ProjectTask | null,
      assigneeId: string | null,
      start: string,
      end: string,
      packageIndex: number,
      packageId: string,
    ) => {
      const startMs = dayMs(start);
      // Ein Ende vor dem Beginn ergibt eine negative Breite, und die reisst die
      // ganze Zeichnung mit: der Balken füllt dann alles, weil aus der Rechnung
      // Unsinn wird. Die Eingabemaske verhindert es, ein Dokument aus fremder
      // Hand nicht - deshalb hier die Untergrenze von einem Tag.
      const endMs = Math.max(dayMs(end), startMs) + DAY_MS;
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;
      // Wer nicht (mehr) im Projekt ist, landet bei den Unzugewiesenen statt in
      // einer Spur, die es nicht gibt.
      const key = collected.has(assigneeId) ? assigneeId : null;
      const left = Math.max(0, percentFor(startMs, range));
      const width = Math.max(1, Math.min(100 - left, percentFor(endMs, range) - left));
      collected.get(key)!.push({ taskId: id, kind, title, task, packageIndex, packageId, leftPercent: left, widthPercent: width });
    };

    packages.forEach((pkg, packageIndex) => {
      place(pkg.id, "package", pkg.name, null, pkg.assigneeId, pkg.start, pkg.end, packageIndex, pkg.id);
      for (const task of pkg.tasks) {
        place(task.id, "task", task.title, task, task.assigneeId, task.start, task.end, packageIndex, pkg.id);
      }
    });
  }

  const order: Array<{ personId: string | null; name: string }> = [
    ...people.map((p) => ({ personId: p.id as string | null, name: p.name })),
    { personId: null, name: unassignedLabel },
  ];

  return order.map(({ personId, name }) => ({
    personId,
    name,
    groups: packIntoGroups(collected.get(personId) ?? []),
  }));
}

/**
 * Greedy nach Startzeit: jeder Balken kommt in die erste Zeile, in der er nicht
 * mit einem bereits liegenden kollidiert. Das ist dieselbe Regel, nach der der
 * Karriere-Graph seine Äste vergibt (@adr SPARK-0023-career-graph-branches-on-overlap@1) - überlappen heißt neue
 * Spur, sonst dieselbe.
 */
function packIntoGroups(bars: LaneBar[]): LaneGroup[] {
  // Erst nach PAKET gruppieren, dann innerhalb des Pakets packen. Ohne diese
  // Stufe entschied allein das Datum, welcher Balken in welche Zeile rutscht,
  // und eine Aufgabe landete über dem Paket einer anderen Gruppe - die Zeichnung
  // stimmte auf den Tag genau und log über die Zugehörigkeit.
  const byPackage = new Map<string, LaneBar[]>();
  for (const bar of bars) {
    const group = byPackage.get(bar.packageId);
    if (group) group.push(bar);
    else byPackage.set(bar.packageId, [bar]);
  }

  const groups: LaneGroup[] = [];
  for (const [packageId, bars] of byPackage) {
    const packageBar = bars.find((b) => b.kind === "package") ?? null;
    const tasks = bars.filter((b) => b.kind !== "package");

    // Greedy nur unter den Zeilen DIESES Pakets: eine Aufgabe darf sich eine
    // Zeile mit einer Schwester teilen, nie mit einer fremden Aufgabe.
    const rows: LaneBar[][] = [];
    for (const bar of [...tasks].sort((a, b) => a.leftPercent - b.leftPercent)) {
      const end = bar.leftPercent + bar.widthPercent;
      const row = rows.find((r) =>
        r.every((b) => b.leftPercent >= end || b.leftPercent + b.widthPercent <= bar.leftPercent),
      );
      if (row) row.push(bar);
      else rows.push([bar]);
    }
    groups.push({ packageId, packageBar, rows });
  }
  return groups;
}

/** Montag der ISO-Woche, in der `ms` liegt. */
function isoWeekStart(ms: number): number {
  const d = new Date(ms);
  // getUTCDay zählt ab Sonntag, ISO 8601 ab Montag - deshalb der Versatz.
  const dayFromMonday = (d.getUTCDay() + 6) % 7;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dayFromMonday);
}

/**
 * ISO-Wochennummer. Die Jahresgrenze entscheidet der DONNERSTAG der Woche: eine
 * Woche gehört zu dem Jahr, in dem ihr Donnerstag liegt (ISO 8601). Ohne diese
 * Regel bekäme der 1. Januar je nach Wochentag mal die 1 und mal die 52 des
 * Vorjahres, und beides wäre falsch.
 */
export function isoWeekNumber(ms: number): number {
  const start = isoWeekStart(ms);
  const thursday = new Date(start + 3 * DAY_MS);
  const jan4 = Date.UTC(thursday.getUTCFullYear(), 0, 4);
  return Math.round((start - isoWeekStart(jan4)) / (7 * DAY_MS)) + 1;
}

export interface WeekTick {
  /** Montag der Woche. */
  startMs: number;
  /** Sonntag der Woche, also der letzte Tag - für die Klartext-Angabe. */
  endMs: number;
  week: number;
  percent: number;
}

/**
 * Wochenanfänge innerhalb der Spanne. Feinere Teilung unter den Monaten, damit
 * man auf der Achse abzählen kann, ohne Tage zu schätzen.
 */
export function weekTicks(range: ScheduleRange): WeekTick[] {
  const out: WeekTick[] = [];
  // Beginnt bei der Woche, die den Anfang ENTHAELT, nicht bei der ersten, die
  // vollstaendig in der Spanne liegt: ein Plan faengt fast nie an einem Montag
  // an, und die Woche, in der er beginnt, ist genau die, deren Nummer man wissen
  // will. Ihre Marke sitzt am linken Rand, ihr Anfang liegt rechnerisch davor.
  let cursor = isoWeekStart(range.startMs);
  while (cursor <= range.endMs) {
    out.push({
      startMs: cursor,
      endMs: cursor + 6 * DAY_MS,
      week: isoWeekNumber(cursor),
      percent: Math.max(0, percentFor(cursor, range)),
    });
    cursor += 7 * DAY_MS;
  }
  return out;
}

/**
 * Jede wievielte Woche eine Beschriftung bekommt. "KW 39" braucht rund 34
 * Pixel; steht alle 42 Pixel eine Zahl, stossen sie aneinander. Statt die
 * Schrift zu verkleinern, bis niemand sie mehr liest, bekommt nur jede zweite
 * oder vierte Woche ihre Zahl - die übrigen bleiben Striche, und die Zahl
 * dazwischen kann man abzählen.
 */
export function weekLabelEvery(pxPerDay: number): number {
  const weekPx = 7 * pxPerDay;
  if (weekPx >= 56) return 1;
  if (weekPx >= 28) return 2;
  return 4;
}

/** Monatsanfänge innerhalb der Spanne - die Beschriftung der Zeitachse. */
export function monthTicks(range: ScheduleRange): { ms: number; percent: number }[] {
  const out: { ms: number; percent: number }[] = [];
  const cursor = new Date(range.startMs);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  while (cursor.getTime() <= range.endMs) {
    const ms = cursor.getTime();
    if (ms >= range.startMs) out.push({ ms, percent: percentFor(ms, range) });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

/** Eine Terminmarke, so weit die Kollisionsrechnung sie kennen muss. */
export interface MarkLabel {
  /** Position der Raute auf der Achse, 0..100. */
  percent: number;
  /** Geschätzte Breite der Beschriftung in Pixeln. */
  labelWidth: number;
  /** Beschriftung links der Raute statt rechts (am rechten Rand der Fall). */
  labelLeft: boolean;
}

/**
 * Verteilt die Beschriftungen auf Stufen, damit sich zwei nahe Termine nicht
 * überschreiben. Stufe 0 liegt unmittelbar über der Achse, jede weitere darüber.
 *
 * Nicht die Raute wandert, sondern nur ihr Text: die Marke selbst MUSS auf ihrem
 * Datum bleiben, sonst zeigt die Zeichnung an die falsche Stelle. Zwei Termine
 * am selben Tag sind ein legitimer Fall - "Abgabe" und "Review" -, und dann
 * stehen die Namen eben übereinander statt ineinander.
 */
export function stackMarkLabels(marks: readonly MarkLabel[], axisWidth: number): number[] {
  const order = marks.map((mark, index) => ({ mark, index })).sort((a, b) => a.mark.percent - b.mark.percent);
  const levels = new Array<number>(marks.length).fill(0);
  // Je Stufe die rechte Kante der zuletzt gesetzten Beschriftung.
  const occupiedUntil: number[] = [];
  for (const { mark, index } of order) {
    const anchor = (mark.percent / 100) * axisWidth;
    const left = mark.labelLeft ? anchor - mark.labelWidth : anchor;
    const right = left + mark.labelWidth;
    let level = 0;
    while (occupiedUntil[level] !== undefined && occupiedUntil[level]! > left) level++;
    occupiedUntil[level] = right;
    levels[index] = level;
  }
  return levels;
}
